import Decimal from 'decimal.js';
import { CONTAINER_PAYLOAD_KG, type ImportPlanResult } from './trade';

/**
 * Kế hoạch nhập khẩu dài hạn (nhánh A XNK, DEC-538, docs/phases/P10/xnk-a-ke-hoach-nhap.md).
 * Nhãn mức khẩn + gom hàng theo NCC thành cont, làm tròn đầy cont bằng hàng sắp phải đặt.
 * Công thức ROP / SS / SL gợi ý: `importPlan` trong `./trade` (PR nền DEC-537).
 */

export type ImportUrgency = ImportPlanResult['urgency'];
export const IMPORT_URGENCIES: readonly ImportUrgency[] = ['order_now', 'order_soon', 'ok', 'no_demand'];
export const IMPORT_URGENCY_LABELS: Record<ImportUrgency, string> = {
  order_now: 'Phải đặt tuần này',
  order_soon: 'Đặt trong 30 ngày',
  ok: 'Đủ hàng',
  no_demand: 'Không bán',
};

/** Cỡ cont chọn được trên màn kế hoạch (thép nặng: 20' thường đầy tải trước khi đầy thể tích). */
export const PLAN_CONTAINER_SIZES = ['20', '40'] as const;
export type PlanContainerSize = (typeof PLAN_CONTAINER_SIZES)[number];

export interface ContainerPlanLine {
  productId: number;
  /** SL đã chốt đặt (gợi ý hoặc NV sửa), ĐVT chính */
  quantity: number;
  kgPerUnit: number | null;
  packQty?: number | null;
  avgDaily: number;
  /** Số ngày tới hạn đặt (null = không bán) */
  orderInDays: number | null;
}

export interface ContainerPlan {
  /** Tổng kg của các dòng có kg/ĐVT */
  totalKg: number;
  /** Số dòng có SL mà chưa biết kg/ĐVT (không tính được cont) */
  unknownKg: number;
  containers: number;
  payloadKg: number;
  /** % đầy của cont cuối (100 khi vừa khít), 0 khi không có hàng */
  lastFillPct: number;
  /** Kg còn trống ở cont cuối */
  spareKg: number;
  /** Hàng gợi ý thêm cho đầy cont: mã sắp phải đặt, tối đa `topUpDays` ngày bán, làm tròn bó */
  topUps: { productId: number; quantity: number; kg: number }[];
  /** Sau khi thêm hàng gợi ý */
  afterTopUp: { totalKg: number; lastFillPct: number; spareKg: number };
}

const r3 = (d: Decimal) => d.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toNumber();

function fill(totalKg: Decimal, payload: number, containers: number) {
  if (containers <= 0) return { lastFillPct: 0, spareKg: 0 };
  const lastKg = totalKg.minus(payload * (containers - 1));
  return {
    lastFillPct: lastKg.div(payload).mul(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber(),
    spareKg: r3(new Decimal(payload * containers).minus(totalKg)),
  };
}

/**
 * Gom các dòng của 1 NCC vào cont cỡ `size`: số cont = ⌈tổng kg / tải cont⌉.
 * Cont cuối còn trống → gợi ý thêm hàng của chính NCC đó, ưu tiên mã tới hạn đặt sớm nhất, mỗi mã tối đa `topUpDays` ngày bán
 * (mặc định 30 = một chu kỳ xem lại), làm tròn xuống theo bó; không vượt chỗ trống.
 */
export function planContainers(lines: ContainerPlanLine[], size: string, topUpDays = 30): ContainerPlan {
  const payloadKg = CONTAINER_PAYLOAD_KG[size] ?? CONTAINER_PAYLOAD_KG['20']!;
  let total = new Decimal(0);
  let unknownKg = 0;
  for (const l of lines) {
    if (!(l.quantity > 0)) continue;
    if (l.kgPerUnit && l.kgPerUnit > 0) total = total.plus(new Decimal(l.quantity).mul(l.kgPerUnit));
    else unknownKg++;
  }
  const containers = total.gt(0) ? total.div(payloadKg).ceil().toNumber() : 0;
  const before = fill(total, payloadKg, containers);
  const topUps: ContainerPlan['topUps'] = [];
  let spare = new Decimal(before.spareKg);
  let after = total;
  if (containers > 0 && spare.gt(0)) {
    const candidates = lines
      .filter((l) => l.avgDaily > 0 && (l.kgPerUnit ?? 0) > 0)
      .sort((a, b) => (a.orderInDays ?? Infinity) - (b.orderInDays ?? Infinity) || a.productId - b.productId);
    for (const l of candidates) {
      if (spare.lte(0)) break;
      const kg = new Decimal(l.kgPerUnit!);
      let qty = Decimal.min(new Decimal(l.avgDaily).mul(topUpDays), spare.div(kg));
      const pack = l.packQty && l.packQty > 0 ? l.packQty : null;
      qty = pack ? qty.div(pack).floor().mul(pack) : qty.floor();
      if (qty.lte(0)) continue;
      const addKg = qty.mul(kg);
      topUps.push({ productId: l.productId, quantity: r3(qty), kg: r3(addKg) });
      spare = spare.minus(addKg);
      after = after.plus(addKg);
    }
  }
  return {
    totalKg: r3(total),
    unknownKg,
    containers,
    payloadKg,
    ...before,
    topUps,
    afterTopUp: { totalKg: r3(after), ...fill(after, payloadKg, containers) },
  };
}

/** Nước hay gặp khi nhập thép / thiết bị (mã ISO 3166-1 alpha-2). Ô nhập vẫn nhận mã khác. */
export const TRADE_COUNTRY_LABELS: Record<string, string> = {
  CN: 'Trung Quốc',
  KR: 'Hàn Quốc',
  JP: 'Nhật Bản',
  TW: 'Đài Loan',
  IN: 'Ấn Độ',
  ID: 'Indonesia',
  MY: 'Malaysia',
  TH: 'Thái Lan',
  SG: 'Singapore',
  US: 'Mỹ',
  DE: 'Đức',
  IT: 'Ý',
  ES: 'Tây Ban Nha',
  TR: 'Thổ Nhĩ Kỳ',
  RU: 'Nga',
  VN: 'Việt Nam',
};
export const countryLabel = (c: string | null | undefined) => (c ? `${TRADE_COUNTRY_LABELS[c] ?? c} (${c})` : '');
